import React, { Fragment, useState, useEffect } from "react";
import { SubLayout } from "@/containers/layout/LandingLayout";
import DashboardButton from "@/atoms/DashboardButton";
import ArticleFilter from "./articleFilter/ArticleFilter";
import { getArticlesPaginatedQuery, getCategoriesQuery } from "@/services/cms/queries";
import { StrapiRequestParams } from "strapi-sdk-js";
import { Article, PaginationMeta, CategoryResponse, Category } from "@/services/cms/apiTypes";
import SingleArticle from "../singleArticle";
import getConfig from "next/config";

export const ARTICLES_DEFAULT_POPULATE = ["categories", "cover", "author", "author.avatar"];
export interface ArticleListProps {
  hideCategories?: boolean;
  baseUrl: string;
  preSelectedCategory?: number;
  excludeCategories?: number[];
}
const ArticleList = (props: ArticleListProps): JSX.Element => {
  const { hideCategories, baseUrl, preSelectedCategory, excludeCategories } = props;
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(undefined);
  const { publicRuntimeConfig } = getConfig();

  const [articlesParams, setArticlesParams] = useState<StrapiRequestParams>({
    populate: ARTICLES_DEFAULT_POPULATE,
    sort: "addedOn:desc",
    ...((preSelectedCategory || selectedCategory || excludeCategories) && {
      filters: {
        categories: {
          id: {
            ...((selectedCategory || preSelectedCategory) && {
              $eq: [...[preSelectedCategory], ...[selectedCategory]],
            }),
            ...(excludeCategories && { $ne: excludeCategories }),
          },
        },
      },
    }),
  });
  const handleLoadMoreArticles = () => {
    fetchNextPage();
  };
  const {
    isLoading: isArticleLoding,
    data: articleResponse,
    hasNextPage,
    fetchNextPage,
  } = getArticlesPaginatedQuery(articlesParams);

  const { isLoading: isCategoriesLoading, data: categoriesResponse } = getCategoriesQuery();

  useEffect(() => {
    if (!isArticleLoding) {
      setArticles(articleResponse?.pages.flatMap((page) => page.data) || []);
    }
  }, [isArticleLoding, articleResponse]);

  useEffect(() => {
    setArticlesParams({
      populate: ARTICLES_DEFAULT_POPULATE,
      sort: "addedOn:desc",
      ...((preSelectedCategory || selectedCategory || excludeCategories) && {
        filters: {
          categories: {
            id: {
              ...((selectedCategory || preSelectedCategory) && {
                $eq: [...[preSelectedCategory], ...[selectedCategory]],
              }),
              ...(excludeCategories && { $ne: excludeCategories }),
            },
          },
        },
      }),
    });
  }, [selectedCategory, preSelectedCategory, excludeCategories]);

  useEffect(() => {
    if (!isCategoriesLoading) {
      setCategories(categoriesResponse?.data.map((category) => category) || []);
    }
  }, [isCategoriesLoading, categoriesResponse]);

  return (
    <SubLayout allowOverflow>
      {!hideCategories && (
        <ArticleFilter
          setSelectedCategory={setSelectedCategory}
          selectedCategory={selectedCategory || 0}
          categories={categories.filter(
            (category) => category.id != publicRuntimeConfig.NEXT_BLOG_LANDLORD_CATEGORY_ID,
          )}
        />
      )}
      <section className="flex flex-wrap items-center justify-left w-auto mt-9 max-w-[90rem] mb-5 md:mb-12 mx-auto lg:px-[3rem] xl:px-[3.75rem]">
        {articles.map((article, idx) => (
          <Fragment key={idx}>
            <div className={`flex flex-col w-full md:w-1/2 lg:w-1/3 mb-8 md:px-4`}>
              <SingleArticle
                baseUrl={baseUrl}
                article={{ id: article.id, attributes: article.attributes }}
              />
            </div>
          </Fragment>
        ))}
      </section>
      {hasNextPage && (
        <div className="flex justify-center mb-5 w-full md:w-auto" onClick={handleLoadMoreArticles}>
          <DashboardButton
            className="px-4 py-2 w-full md:w-auto rounded-sm md:rounded-lg"
            variant="submit"
          >
            Load more articles
          </DashboardButton>
        </div>
      )}
    </SubLayout>
  );
};

export default ArticleList;
